// 權限定義
const PERMISSIONS = {
  // 產品資料管理
  PRODUCT_BASIC_EDIT: 'product.basic.edit',           // 產品基本資訊編輯
  PRODUCT_LOGISTICS_EDIT: 'product.logistics.edit',   // 物流與物理屬性編輯
  PRODUCT_COST_VIEW: 'product.cost.view',             // 成本與階梯價格查看
  PRODUCT_HK_VIEW: 'product.hk.view',                 // 香港專用欄位查看
  PRODUCT_MARKETING_EDIT: 'product.marketing.edit',   // 行銷與目錄狀態編輯（目錄/官網狀態）
  // 需求 R4（PM 2026-05-08 補充指示）：
  // 採購需可編輯「產品狀態(上架/下架)」與「香港不出」兩個欄位，
  // 但不應一併放開目錄/官網狀態。故將「產品狀態」與「香港不出」拆出獨立權限。
  PRODUCT_STATUS_EDIT: 'product.status.edit',         // 產品狀態（上架/下架）編輯
  PRODUCT_HK_EXCLUDED_EDIT: 'product.hk.excluded.edit', // 香港不出（不銷售至香港）編輯
  // 需求 R157（PM 2026-09-01 拍板 Q2=A）：權限鍵保留，不改成寫死 true，
  // 只是三個角色陣列都列入 —— 日後若要再收回，改角色陣列一行即可，兩處入口不必動。
  PRODUCT_AUDIT_VIEW: 'product.audit.view',           // 稽核紀錄查看（manager/purchasing/sales 全開）

  // 報價單操作
  QUOTE_CREATE: 'quote.create',                       // 建立報價單
  QUOTE_EDIT: 'quote.edit',                           // 編輯報價單
  QUOTE_MARKUP: 'quote.markup',                       // 全單加成/單品加價/貼標費
  QUOTE_PRICE_VIEW: 'quote.price.view',               // 四階梯價格預覽
  QUOTE_FINAL_ADJUST: 'quote.final.adjust',           // 最終報價金額手動調整

  // 流程控管
  QUOTE_APPROVE: 'quote.approve',                     // 審核報價單

  // 客戶管理
  CUSTOMER_MANAGE: 'customer.manage',                 // 建立/編輯客戶

  // 帳號管理
  USER_MANAGE: 'user.manage',                         // 管理用戶
  USER_DELETE: 'user.delete',                         // 刪除用戶
  USER_PASSWORD_ALL: 'user.password.all',             // 修改所有人密碼
};

// 角色權限映射
const ROLE_PERMISSIONS = {
  manager: [
    // 產品管理 - 全部執行權限
    PERMISSIONS.PRODUCT_BASIC_EDIT,
    PERMISSIONS.PRODUCT_LOGISTICS_EDIT,
    PERMISSIONS.PRODUCT_COST_VIEW,
    PERMISSIONS.PRODUCT_HK_VIEW,
    PERMISSIONS.PRODUCT_MARKETING_EDIT,
    PERMISSIONS.PRODUCT_STATUS_EDIT,
    PERMISSIONS.PRODUCT_HK_EXCLUDED_EDIT,
    PERMISSIONS.PRODUCT_AUDIT_VIEW,
    
    // 報價單 - 全部權限
    PERMISSIONS.QUOTE_CREATE,
    PERMISSIONS.QUOTE_EDIT,
    PERMISSIONS.QUOTE_MARKUP,
    PERMISSIONS.QUOTE_PRICE_VIEW,
    PERMISSIONS.QUOTE_FINAL_ADJUST,
    PERMISSIONS.QUOTE_APPROVE,
    
    // 客戶管理
    PERMISSIONS.CUSTOMER_MANAGE,
    
    // 帳號管理
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.USER_PASSWORD_ALL,
  ],
  
  purchasing: [
    // 產品管理 - 基本資訊和物流可編輯
    PERMISSIONS.PRODUCT_BASIC_EDIT,
    PERMISSIONS.PRODUCT_LOGISTICS_EDIT,
    PERMISSIONS.PRODUCT_COST_VIEW,
    PERMISSIONS.PRODUCT_HK_VIEW,
    // 需求 R4（PM 2026-05-08 補充指示）：
    // 採購可在產品詳細頁的「行銷與目錄狀態」編輯產品狀態(上架/下架)與「香港不出」勾選；
    // 但目錄/官網狀態仍維持只有主管可改，故不放 PRODUCT_MARKETING_EDIT。
    PERMISSIONS.PRODUCT_STATUS_EDIT,
    PERMISSIONS.PRODUCT_HK_EXCLUDED_EDIT,
    // 需求 R157（PM 2026-09-01 拍板 Q1=A「manager + purchasing + sales
    // 三個角色全開」）**推翻** 需求 R146 時代「稽核紀錄只有主管看得到」的現況。
    // Q4=A：不綁 PRODUCT_COST_VIEW 當前提，單純全角色可看。
    // Q5=A：與 需求 R151「採購看不到歷史報價區塊」互不相干——那是報價單金額，
    // 這裡是產品成本異動歷程，採購本來就有 PRODUCT_COST_VIEW 看得到現值，
    // 本票多開的只是「歷史值與修改人」。
    // 要再收回請改本行，不要回頭改 ProductDetailPage／ProductModal 的 canAuditView。
    PERMISSIONS.PRODUCT_AUDIT_VIEW,
  ],

  sales: [
    // 產品管理 - 只能查看
    PERMISSIONS.PRODUCT_COST_VIEW,
    PERMISSIONS.PRODUCT_HK_VIEW,
    // 需求 R157（PM 2026-09-01 Q1=A）：業務同採購，稽核紀錄一併開放。
    // 僅「查看」，不含任何編輯權限；顯示範圍仍是 需求 R146 鎖住的成本 3 類。
    PERMISSIONS.PRODUCT_AUDIT_VIEW,

    // 報價單 - 建立和編輯
    PERMISSIONS.QUOTE_CREATE,
    PERMISSIONS.QUOTE_EDIT,
    PERMISSIONS.QUOTE_MARKUP,
    PERMISSIONS.QUOTE_PRICE_VIEW,
    PERMISSIONS.QUOTE_FINAL_ADJUST,
    
    // 客戶管理
    PERMISSIONS.CUSTOMER_MANAGE,
  ],
};

// 檢查用戶是否有特定權限
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

// 檢查用戶是否有任一權限
export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.some(permission => hasPermission(userRole, permission));
};

// 檢查用戶是否有所有權限
export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.every(permission => hasPermission(userRole, permission));
};

// 獲取用戶所有權限
export const getUserPermissions = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || [];
};

export { PERMISSIONS };
